import React, { forwardRef } from "react";
import {
  Grid,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  GlobalSearchFormattedData,
  GlobalSearchSelectOption,
} from "data/@types/GlobalSearchData";
import { useGlobalSearchContext } from "./SearchProvider";
import { styled } from "@mui/material/styles";
import AppIcon from "ui/components/icons/AppIcon";
import { iconSearch } from "ui/components/icons";

type SearchTextProps = {
  isLoading: boolean;
  globalSearchSelectedItem: GlobalSearchSelectOption;
  items: GlobalSearchFormattedData[] | null;
};

const SearchTextField = styled(TextField)(({ theme }) => ({
  "& .MuiFormHelperText-root": {
    fontStyle: "italic",
    fontSize: "10px",
    color: "text.secondary",
  },
  "& .MuiInputBase-root": {
    "&:hover:not(.Mui-focused)": {
      "& svg": {
        color: theme.palette.text.primary,
      },
    },
  },
  "& .MuiInput-underline": {
    "&:before": {
      borderBottomColor: theme.palette.text.secondary,
    },
    "&:after": {
      borderBottomColor: theme.palette.common.caBlue!.lighter,
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottomColor: theme.palette.text.primary,
      borderBottomWidth: "2px",
    },
  },
}));

const SearchText = forwardRef((props: SearchTextProps, ref) => {
  const { isLoading, globalSearchSelectedItem, items } = props;

  const { search, setSearch } = useGlobalSearchContext();

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const getHint = () => {
    if (isLoading || !search) return " ";

    if (globalSearchSelectedItem === "All") {
      return getOverallHint();
    }

    return getItemHint();
  };

  const getOverallHint = () => {
    const itemsCount = items!.reduce(
      (partialSum: number, item: GlobalSearchFormattedData) =>
        partialSum + (item.content.length || 0),
      0,
    );

    if (itemsCount > 0) {
      let only = "";
      let refineSearch = "";

      if (hasPartialResult()) {
        only = "only ";
        refineSearch = ", please refine your search terms";
      }

      const itemWord = itemsCount === 1 ? "item" : "items";
      return `Showing ${only}${itemsCount} ${itemWord} across all categories${refineSearch}`;
    }

    return "No results found";
  };

  const getItemHint = () => {
    const item = items!.filter(
      (item) => item.name === globalSearchSelectedItem,
    )[0];

    const { top, refineSearch, onlyPartialMatchesFeedback } =
      getSearchHints(item);
    const spot = item.name.toUpperCase();
    const amount = item.content.length;
    const itemsWord = amount === 1 ? "item" : "items";
    if (amount) {
      return `Showing ${top}${amount} ${itemsWord} in ${spot}${refineSearch}\n${onlyPartialMatchesFeedback}`;
    }

    return `No results found in ${spot}`;
  };

  const getSearchHints = (item: GlobalSearchFormattedData) => {
    let top = "";
    let refineSearch = "";
    let onlyPartialMatchesFeedback = "";

    if (item.total > item.content.length) {
      top = "the top ";
      refineSearch = `. To view additional records that match your search criteria, please refine your search.
       Advanced filters are applied only to the results currently shown on screen.`;
    }

    if (item.hasOnlyPartialMatches) {
      onlyPartialMatchesFeedback =
        "Your search did not return any exact or complete matches. The results shown contain one or more of your search terms and may be less relevant.";
    }

    return { top, refineSearch, onlyPartialMatchesFeedback };
  };

  const hasPartialResult = () => {
    return items!.some((item) => item.total > item.content.length);
  };

  return (
    <Grid container sx={{ py: "12px", width: 1 }}>
      <Grid size={{ xs: 12, sm: 10, md: 8, lg: 6, xl: 4 }}>
        <SearchTextField
          fullWidth
          value={search}
          onChange={onChange}
          placeholder="Search for Firms, Products/Strategies, Comments, Meetings"
          helperText={getHint()}
          autoFocus
          size="small"
          inputRef={ref}
          InputProps={{
            sx: {
              color: "text.primary",
              fontSize: 12,
              letterSpacing: "1.5px",
              fontWeight: 100,
              paddingBottom: 1,
            },
            startAdornment: (
              <InputAdornment position="start">
                <AppIcon
                  icon={iconSearch}
                  fontSize="small"
                  sx={{ color: "common.caBlue.lighter" }}
                />
              </InputAdornment>
            ),
            endAdornment: isLoading && search !== "" && (
              <CircularProgress
                size={20}
                sx={{ color: "common.caBlue.lighter" }}
              />
            ),
          }}
          variant="standard"
          data-test-id="gs-text-input"
        />
      </Grid>
    </Grid>
  );
});

SearchText.displayName = "SearchText";

export default SearchText;
